import React from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PERMISSION_SECTIONS } from '../../utils/permissions';

function getSectionSelectionState(sectionPermissions, selectedPermissions) {
  const selectedCount = sectionPermissions.filter((p) => selectedPermissions.includes(p)).length;
  if (selectedCount === 0) return { checked: false, indeterminate: false };
  if (selectedCount === sectionPermissions.length) return { checked: true, indeterminate: false };
  return { checked: false, indeterminate: true };
}

function PermissionSectionsPicker({
  availablePermissions,
  selectedPermissions,
  onTogglePermission,
  onToggleSection,
  emptyMessage,
}) {
  const { t } = useTranslation();

  if (!availablePermissions?.length) {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        {emptyMessage}
      </Alert>
    );
  }

  const sections = PERMISSION_SECTIONS
    .map((section) => ({
      ...section,
      permissions: section.permissions.filter((p) => availablePermissions.includes(p)),
    }))
    .filter((section) => section.permissions.length > 0);

  return (
    <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
      {sections.map((section) => {
        const { checked, indeterminate } = getSectionSelectionState(
          section.permissions,
          selectedPermissions,
        );
        return (
          <Box key={section.key} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                size="small"
                checked={checked}
                indeterminate={indeterminate}
                onChange={() => onToggleSection(section.permissions)}
              />
              <Typography variant="overline" color="text.secondary">
                {t(`permissionSections.${section.key}`)}
              </Typography>
            </Box>
            <Divider sx={{ mb: 1 }} />
            <FormGroup sx={{ pl: 2 }}>
              {section.permissions.map((permission) => (
                <FormControlLabel
                  key={permission}
                  control={(
                    <Checkbox
                      size="small"
                      checked={selectedPermissions.includes(permission)}
                      onChange={() => onTogglePermission(permission)}
                    />
                  )}
                  label={(
                    <Typography variant="body2">
                      {t(`permissions.${permission}`, permission)}
                    </Typography>
                  )}
                />
              ))}
            </FormGroup>
          </Box>
        );
      })}
    </Box>
  );
}

export default PermissionSectionsPicker;
